/**
 * Feature toggles for the Care portal.
 *
 * These were previously evaluated at runtime via Unleash
 * (`@unleash/proxy-client-react`). The portal only ever consumed simple on/off
 * booleans — no targeting, variants or gradual rollout — so the flags now live
 * here as deploy-time constants. Flipping one requires a code change + redeploy
 * (there is no longer any runtime toggling). See FG-1380.
 *
 * The values below mirror the Unleash production environment at time of
 * migration.
 *
 * When adding a new toggle, add its key here and read it via `isFeatureEnabled`
 * (or the `useFlag` alias) rather than introducing another flag mechanism.
 */
export const featureFlags: Readonly<Record<string, boolean>> = {
  "portal.activity.all": true,
  "portal.automations.create": false,
  "portal.claims.create": true,
  "portal.claims.createinspection": false,
  "portal.invitations.existingorganisation": true,
  "portal.maintenance_banner_enabled": false,
  "portal.quotebutton": true,
  "portal.scheme_redirect": true,
  "portal.toolkits.create": true,
};

/** Whether a feature flag is enabled. Unknown flags default to `false`. */
export const isFeatureEnabled = (name: string): boolean =>
  featureFlags[name] ?? false;

/**
 * Drop-in replacement for the former Unleash `useFlag` hook so existing call
 * sites only need an import-path change. Flags are static, so this is a plain
 * synchronous lookup — it is not a real hook and is not subject to the rules of
 * hooks.
 */
export const useFlag = (name: string): boolean => isFeatureEnabled(name);
